import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import { PreviewEmployeesModalColumn } from "@/pages/employees/employee/widgets/preview-employees-modal.types.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { UserCard } from "@/components/user-card/UserCard.tsx";
import { useEmployees } from "@/queries/employees/use-employees.query.ts";
import { ApiSortingDirection } from "@/types/api.types.ts";
import { EmployeeColumnId } from "@/types/column.types.ts";
import {
  ApiEmployeeFilter,
  getActiveEmployeeLifecycleStatuses,
} from "@/types/employees/employee.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { Box, FontAwesomeIcon, Skeleton, Stack, Title, useModals } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const HomeUpcomingBirthdays = () => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { renderEmployeeName, renderPartialDate } = useFormatter();
  const { canIEdit } = usePermissions();

  const canViewEmployees = canIEdit(ApiPermission.see_employee_directory);

  const upcomingBirthdaysFilter: ApiEmployeeFilter = {
    page_number: 1,
    sorting: {
      fields: [
        {
          field: EmployeeColumnId.birth_date_relative,
          direction: ApiSortingDirection.asc,
        },
      ],
    },
    employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
  };
  const { data: upcomingBirthdays, isLoading: isUpcomingBirthdays } = useEmployees(
    upcomingBirthdaysFilter,
    canViewEmployees
  );

  const filteredUpcomingBirthdays = useMemo(() => {
    const today = new Date();
    const oneMonthFromNow = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());

    return upcomingBirthdays?.items.filter((employee) => {
      if (!employee.birth_date?.month || !employee.birth_date?.day) return false;

      const birthdayDate = new Date(
        today.getFullYear(),
        employee.birth_date.month - 1,
        employee.birth_date.day
      );

      if (birthdayDate < today) {
        birthdayDate.setFullYear(today.getFullYear() + 1);
      }

      return birthdayDate >= today && birthdayDate <= oneMonthFromNow;
    });
  }, [upcomingBirthdays]);

  if (isUpcomingBirthdays) {
    return (
      <Box className={"pt-10"}>
        <Skeleton className={"w-full h-64"} />
      </Box>
    );
  }

  if (!filteredUpcomingBirthdays || filteredUpcomingBirthdays.length === 0) return null;

  return (
    <>
      <Title
        header={t("core:upcoming_birthdays")}
        size={"lg"}
        paddingBottom
        paddingTop
        actionIcon={<FontAwesomeIcon icon={"fa-solid fa-chevron-right"} />}
        onClick={() => {
          openModal(PreviewEmployeesModal, {
            filter: upcomingBirthdaysFilter,
            addColumns: [PreviewEmployeesModalColumn.birth_date],
            modalTitle: t("core:upcoming_birthdays"),
            source: "Home: Upcoming birthdays",
          });
        }}
      />

      <Stack direction={"horizontal"} gap={"md"}>
        {filteredUpcomingBirthdays.slice(0, 5).map((birthdayEmployee) => (
          <UserCard
            size={"md"}
            onClick={() => {
              openModal(EmployeeViewModal, {
                employeeId: birthdayEmployee.id,
              });
            }}
            className={"self-start cursor-pointer"}
            key={`birthday-${birthdayEmployee.id}`}
            to={`/employees/${birthdayEmployee.id}`}
            title={renderEmployeeName(birthdayEmployee)}
            caption={t(
              `${t("core:birthday")}\n${birthdayEmployee.birth_date ? renderPartialDate(birthdayEmployee.birth_date) : ""}`
            )}
            avatarElement={<EmployeeAvatar size={"2xl"} employee={birthdayEmployee} />}
          />
        ))}
      </Stack>
    </>
  );
};
